import { buildPollinationsImageUrl } from '../imagegen/pollinationsClient';
import { normalizeHex } from './colorSystem';

function safeText(s) {
  return String(s || '').trim();
}

function dataUrlFromBlob(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => resolve('');
    r.readAsDataURL(blob);
  });
}

function sanitizeReferenceImageForUrl(referenceImage) {
  const s = String(referenceImage || '').trim();
  if (!s) return undefined;
  const lower = s.toLowerCase();

  // Pollinations client mode puts referenceImage in a query param (`image=`).
  // Data URLs are huge and can easily trigger HTTP 414 (URI Too Long) in browsers/proxies.
  if (lower.startsWith('data:')) return undefined;
  if (lower.startsWith('data%3a')) return undefined;
  if (lower.includes('base64,')) return undefined;

  // Defensive: avoid extremely long URLs in query params.
  if (s.length > 900) return undefined;

  return s;
}

async function fetchAsDataUrl(url) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(t || `HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    return await dataUrlFromBlob(blob);
  } catch {
    // Fallback: if `fetch()` is blocked (common in client-only mode due to CORS),
    // return the URL so the UI can still preview via <img src="...">.
    return String(url || '');
  }
}

async function loadImageFromDataUrl(dataUrl) {
  const src = String(dataUrl || '').trim();
  if (!src) return null;
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function sampleCornerColor(data, w, x0, y0, size = 6) {
  const s = Math.max(1, Math.min(size, w));
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let y = y0; y < y0 + s; y++) {
    for (let x = x0; x < x0 + s; x++) {
      const i = (y * w + x) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n += 1;
    }
  }
  if (!n) return { r: 255, g: 255, b: 255 };
  return { r: r / n, g: g / n, b: b / n };
}

function distRgb(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

async function removeSolidBackground(dataUrl, { threshold = 34, feather = 18 } = {}) {
  const img = await loadImageFromDataUrl(dataUrl);
  if (!img) return dataUrl;
  const w = img.naturalWidth || 512;
  const h = img.naturalHeight || 512;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  const corners = [
    sampleCornerColor(d, w, 0, 0),
    sampleCornerColor(d, w, w - 6, 0),
    sampleCornerColor(d, w, 0, h - 6),
    sampleCornerColor(d, w, w - 6, h - 6),
  ];
  const bg = {
    r: (corners[0].r + corners[1].r + corners[2].r + corners[3].r) / 4,
    g: (corners[0].g + corners[1].g + corners[2].g + corners[3].g) / 4,
    b: (corners[0].b + corners[1].b + corners[2].b + corners[3].b) / 4,
  };

  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 0) continue;
    const px = { r: d[i], g: d[i + 1], b: d[i + 2] };
    const dist = distRgb(px, bg);
    if (dist <= threshold) {
      d[i + 3] = 0;
      continue;
    }
    if (dist <= threshold + feather) {
      const t = (dist - threshold) / Math.max(1, feather);
      d[i + 3] = Math.round(a * t);
    }
  }

  ctx.putImageData(imageData, 0, 0);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return dataUrl;
  }
}

export function makeHighlightIconPrompt({
  conceptEn,
  conceptAr,
  palette,
  style = 'mini_illustration', // mini_illustration | sub_logo
  brandName,
  industry,
  tone,
}) {
  const en = safeText(conceptEn);
  const ar = safeText(conceptAr);
  const subject = [en, ar].filter(Boolean).join(' / ');

  const brandLine = [safeText(brandName), safeText(industry), safeText(tone)].filter(Boolean).join(' • ');

  const pal = Array.isArray(palette) ? palette.map(normalizeHex).filter(Boolean).slice(0, 5) : [];
  const paletteLine = pal.length ? `Use only these colors (or close shades): ${pal.join(', ')}.` : '';

  const styleLine =
    style === 'sub_logo'
      ? 'Create a sub-logo mark (logo symbol variation) for this category, consistent with the main brand logo style.'
      : 'Mini illustration icon style, modern, clean, consistent, centered.';

  return [
    style === 'sub_logo'
      ? `Create a logo symbol variation representing: ${subject}.`
      : `Create a single icon representing: ${subject}.`,
    brandLine ? `Keep it consistent with this brand identity: ${brandLine}.` : 'Keep it consistent with the brand identity.',
    styleLine,
    'No text, no letters, no words.',
    'Transparent background.',
    'High contrast, simple shapes, suitable for Instagram highlight cover.',
    paletteLine,
    style === 'sub_logo'
      ? 'Logo-like: minimal, vector-like, scalable, centered. Do NOT copy the exact main logo; make a related symbol.'
      : 'Mini illustration (not flat), clean, centered subject, subtle shading, no background scene.',
    'Vector-like, crisp edges, not photorealistic.',
  ]
    .filter(Boolean)
    .join('\n');
}

export function makeBrandLogoPrompt({ brandName, industry, tone, palette }) {
  const name = safeText(brandName);
  const ind = safeText(industry);
  const t = safeText(tone);
  const brandLine = [name, ind, t].filter(Boolean).join(' • ');

  const pal = Array.isArray(palette) ? palette.map(normalizeHex).filter(Boolean).slice(0, 5) : [];
  const paletteLine = pal.length ? `Use only these colors (or close shades): ${pal.join(', ')}.` : '';

  return [
    name ? `Create a premium logo mark for the brand: ${name}.` : 'Create a premium logo mark for a brand.',
    brandLine ? `Brand identity: ${brandLine}.` : '',
    'Logo mark only (no wordmark).',
    'No text, no letters, no words.',
    'Transparent background.',
    paletteLine,
    'Vector-like, crisp edges, scalable, centered, high contrast.',
    'Modern, minimal, not photorealistic.',
  ]
    .filter(Boolean)
    .join('\n');
}

export async function generateHighlightIconDataUrl({
  conceptEn,
  conceptAr,
  palette,
  brandName,
  industry,
  tone,
  style,
  apiKey,
  useProxy,
  seed,
  referenceImage,
}) {
  const prompt = makeHighlightIconPrompt({
    conceptEn,
    conceptAr,
    palette,
    brandName,
    industry,
    tone,
    style: style || 'mini_illustration',
  });

  const safeReferenceImage = sanitizeReferenceImageForUrl(referenceImage);
  const url = buildPollinationsImageUrl({
    prompt,
    model: 'flux',
    width: 512,
    height: 512,
    seed: seed || undefined,
    enhance: false,
    negative_prompt:
      'text, watermark, letters, words, blurry, low quality, photo, photorealistic, background, frame, border',
    safe: true,
    quality: 'high',
    transparent: true,
    apiKey: useProxy ? undefined : apiKey || undefined,
    referenceImage: safeReferenceImage,
    useProxy: Boolean(useProxy),
  });

  // Ensure absolute (proxy mode might return relative).
  const abs = new URL(url, window.location.origin).toString();
  const dataUrl = await fetchAsDataUrl(abs);
  const cleaned = await removeSolidBackground(dataUrl).catch(() => dataUrl);
  return { dataUrl: cleaned, prompt };
}

export async function generateBrandLogoDataUrl({ brandName, industry, tone, palette, apiKey, useProxy, seed }) {
  const prompt = makeBrandLogoPrompt({ brandName, industry, tone, palette });
  const url = buildPollinationsImageUrl({
    prompt,
    model: 'flux',
    width: 768,
    height: 768,
    seed: seed || undefined,
    enhance: false,
    negative_prompt:
      'text, watermark, letters, words, blurry, low quality, photo, photorealistic, background, frame, border',
    safe: true,
    quality: 'high',
    transparent: true,
    apiKey: useProxy ? undefined : apiKey || undefined,
    referenceImage: undefined,
    useProxy: Boolean(useProxy),
  });

  const abs = new URL(url, window.location.origin).toString();
  const dataUrl = await fetchAsDataUrl(abs);
  const cleaned = await removeSolidBackground(dataUrl).catch(() => dataUrl);
  return { dataUrl: cleaned, prompt };
}
